import type { ReactNode } from "react";

import type { Currency } from "@krea/shared/types/common";

import { BidTypeEnum } from "@/features/common/enums";
import type {
  IBid,
  ICompanyLoanBid,
  IFactoringBid,
  ILeasingBid,
  IPurchaseFinancingBid,
  IRealEstateBid,
  IRevolvingCreditBid,
  UnionBidInput,
} from "@/features/lender-responses/types/lender-response";

import { LoanBid } from "./bid-types/company-loan/LoanBid";
import { FactoringBid } from "./bid-types/factoring/FactoringBid";
import { LeasingBid } from "./bid-types/leasing/LeasingBid";
import { PurchaseFinancingBid } from "./bid-types/purchase-financing/PurchaseFinancingBid";
import { RealEstateBid } from "./bid-types/real-estate/RealEstateBid";
import { RevolvingCreditBid } from "./bid-types/revolving-credit/RevolvingCreditBid";

export interface IBidDetailsResponse {
  summary: ReactNode;
  details: ReactNode;
}

export interface IBidDetailsProps {
  bid: IBid | UnionBidInput;
  currency: Currency;
}

const getBidInfo = ({
  bid,
  currency,
}: IBidDetailsProps): IBidDetailsResponse => {
  switch (bid.type) {
    case BidTypeEnum.COMPANY_LOAN:
      return LoanBid({ bid: bid as ICompanyLoanBid, currency });

    case BidTypeEnum.FACTORING:
      return FactoringBid({
        bid: bid as IFactoringBid,
        currency,
      }); // TODO: get numberOfInvoices from applying company
    case BidTypeEnum.REAL_ESTATE:
      return RealEstateBid({ bid: bid as IRealEstateBid, currency });
    case BidTypeEnum.LEASING:
      return LeasingBid({ bid: bid as ILeasingBid, currency });
    case BidTypeEnum.REVOLVING_CREDIT:
      return RevolvingCreditBid({
        bid: bid as IRevolvingCreditBid,
        currency,
      });
    case BidTypeEnum.PURCHASE_FINANCING:
      return PurchaseFinancingBid({
        bid: bid as IPurchaseFinancingBid,
        currency,
      });
  }
};

export const BidDetails = ({ bid, currency }: IBidDetailsProps) => {
  const response = getBidInfo({ bid, currency });

  return (
    <div className="rounded-md border border-solid border-gray-300 bg-white">
      <div className="rounded-md bg-white p-4">{response.details}</div>
    </div>
  );
};

export const BidSummary = ({ bid, currency }: IBidDetailsProps) => {
  const response = getBidInfo({ bid, currency });

  return response.summary;
};
